import { gql, useQuery } from '@apollo/client';
import { useAuthContext } from 'providers/auth';
import { MyCompanyHasLeads } from 'types/apollo/MyCompanyHasLeads';

export const MyCompanyHasLeadsQuery = gql`
  query MyCompanyHasLeads {
    myCompanyHasLeads
  }
`;

/**
 * Whether to show a client the first-run story — "nothing has happened yet, here is what will
 * appear" — rather than blaming their filters. The two look identical on an empty page and mean
 * opposite things, and no page can tell them apart from its own empty result.
 *
 * Internal users are never first-run: they pick a company rather than belong to one, and the query
 * is scoped by an `x-company-id` header they do not send. Skipped rather than filtered afterwards,
 * so `loading` is false immediately instead of spinning on a foregone `false`.
 */
export const useCompanyHasLeads = (): { isFirstRun: boolean; loading: boolean } => {
  const { isInternal } = useAuthContext();

  const { data, error, loading } = useQuery<MyCompanyHasLeads>(MyCompanyHasLeadsQuery, {
    skip: isInternal,
    // Cached for the session, and a cached `false` IS allowed to go stale: a client who receives
    // their first lead keeps seeing first-run copy until a reload. Accepted deliberately —
    // conversations.tsx calls this on every visit, and the query is a sequential scan until
    // leads_v2.company_fk is indexed (ClickUp 868mqdnex). Revisit once that lands.
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  // Only an explicit `false` makes a client first-run. A failed read leaves `data` undefined, and
  // treating that as "never had activity" would replace a working page with "nothing yet".
  return {
    isFirstRun: !isInternal && !loading && !error && data?.myCompanyHasLeads === false,
    loading,
  };
};
